/**
 * SearchBar - Top center search bar floating over the globe.
 *
 * Part of the designer's trip-detail UI layout.
 * Position: fixed, top center
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { MapPin, Search, X } from 'lucide-react'
import { useBasicPlaceSearch } from '@/domain/map'
import { extractPlacesFromResult } from '@/lib/searchResults'
import { create } from '@bufbuild/protobuf'
import { PlaceSearchSchema } from '@generated/proto/model'

import type { Place } from '@generated/proto/model'

interface SearchBarProps {
  onPlaceSelect?: (place: Place) => void
  disabled?: boolean
}

export function SearchBar({
  onPlaceSelect,
  disabled,
}: SearchBarProps) {
  const [inputValue, setInputValue] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [searchResults, setSearchResults] = useState<Place[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const blurTimeoutRef = useRef<number | null>(null)
  const { searchPlaces } = useBasicPlaceSearch()

  // Cleanup blur timeout on unmount
  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current)
      }
    }
  }, [])

  const handleSearch = useCallback(async () => {
    if (!inputValue.trim()) return

    setIsSearching(true)
    try {
      const search = create(PlaceSearchSchema, {
        query: inputValue.trim(),
        maxResults: 10,
        languageCode: 'en',
        regionCode: 'US',
      })
      const response = await searchPlaces(search)
      const places = extractPlacesFromResult(response)
      setSearchResults(places)
      setIsOpen(true)
    } catch (error) {
      console.error('Search failed:', error)
      setSearchResults([])
    } finally {
      setIsSearching(false)
    }
  }, [inputValue, searchPlaces])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSearch()
    }
    if (e.key === 'Escape') {
      setIsOpen(false)
    }
  }

  const handleSelectPlace = (place: Place) => {
    onPlaceSelect?.(place)
    setInputValue(place.displayName || place.name || '')
    setIsOpen(false)
  }

  const handleClear = () => {
    setInputValue('')
    setSearchResults([])
    setIsOpen(false)
    inputRef.current?.focus()
  }

  return (
    <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-40 pointer-events-auto">
      <div className="relative">
        {/* Search Input */}
        <div className="bg-black/50 backdrop-blur-md rounded-full shadow-lg px-4 py-2 min-w-[320px] flex items-center gap-2 border border-white/20">
          {isSearching ? (
            <div className="w-4 h-4 border-2 border-white/30 border-t-white/80 rounded-full animate-spin flex-shrink-0" />
          ) : (
            <Search className="w-4 h-4 text-white/60 flex-shrink-0" />
          )}
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => searchResults.length > 0 && setIsOpen(true)}
            onBlur={() => {
              blurTimeoutRef.current = window.setTimeout(() => setIsOpen(false), 200)
            }}
            placeholder="Search places..."
            disabled={disabled}
            className="flex-1 bg-transparent text-white placeholder:text-white/50 outline-none text-base"
            style={{ fontFamily: "var(--font-alga), sans-serif" }}
          />
          {inputValue && (
            <button
              onClick={handleClear}
              className="text-white/60 hover:text-white/90 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Search Results Dropdown */}
        {isOpen && searchResults.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-black/80 backdrop-blur-md rounded-lg border border-white/20 overflow-hidden shadow-2xl max-h-[300px] overflow-y-auto">
            {searchResults.map((place, index) => (
              <button
                key={place.placeId || `search-result-${index}`}
                onClick={() => handleSelectPlace(place)}
                className="w-full px-4 py-3 text-left transition-colors flex items-start gap-3 text-white/80 hover:bg-white/10"
                style={{ fontFamily: "var(--font-alga), sans-serif" }}
              >
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-white/50" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate text-base text-white">
                    {place.displayName || place.name}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
